import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SelectedActivityResponseData } from '../../interfaces/selectedActivityResponse';
import {
    ActivityData,
    ClassroomStatus,
    CreateActivityData,
} from '../../interfaces/activity';
import { HandleRequestData } from '../../../common/interfaces/handleRequest';
import scheduleManagementApi from '../../api/scheduleManagementApi';
import supergraphApi from '../../../common/api/supergraphApi';
import {
    ActivityDetailEnrichment,
    LearnerAttendance,
    RosterEntry,
} from '../../../common/interfaces/supergraph';
import { AppDispatch, GlobalState } from '../../../main/store';
import { GenericStoreState } from '../../../common/interfaces/genericStoreState';
import { CreateActivityResponseData } from '../../interfaces/createActivityResponse';
import { ApiError } from '../../../common/classes/ApiError';
import { ScheduleManagementAPIQueryParams } from '../../interfaces/queryParams';
import { checkFeature } from '../../../common/utils/featureFlag';
import { isSupergraphEnrichmentEnabled } from '../../../common/utils/supergraphFeature';
import { Customer } from '../../interfaces/activity';
import {
    getCoursesList,
    selectAllActiveCourses,
    selectIsLoaded as selectIsCoursesLoaded,
} from '../../../common/store/slices/coursesSlice';
import { CourseItemData } from '../../../common/interfaces/businessDataItem/courseItem';

// Exporting for easier testing
export const extractOrderItemVersions = (customers?: Array<Customer>) => {
    return (
        customers
            ?.filter(
                (customer) =>
                    customer.order_item_id && customer.order_item_version,
            )
            .map((customer) => ({
                order_item_id: customer.order_item_id!,
                order_item_version: customer.order_item_version!,
                sfdc_line_item_id: customer.sfdc_line_item_id || undefined,
            })) || []
    );
};

export const mapVersionsToCustomers = (
    customers: Array<Customer>,
    orderItemVersions: Array<{
        order_item_id: string;
        order_item_version: number;
        sfdc_line_item_id?: string;
    }>,
) => {
    return customers.map((customer) => {
        const versionInfo = orderItemVersions.find(
            (v) => v.order_item_id === customer.order_item_id,
        );
        return versionInfo
            ? {
                  ...customer,
                  order_item_version: versionInfo.order_item_version,
              }
            : customer;
    });
};

/**
 * Backend-owned classroom / vILT provisioning fields that the classroom status
 * poll is responsible for refreshing. These are populated and mutated by
 * downstream services (Classrooms, VCMS/WebEx) rather than edited by the user,
 * and are already stripped from the payload on update in updateSelectedActivity.
 * The poll patches ONLY these fields so it never overwrites user-editable fields
 * (e.g. billing_status, class_size, attended) with an eventually-consistent read.
 */
export const CLASSROOM_STATUS_POLL_FIELDS = [
    'classrooms_status',
    'classrooms_arn',
    'classrooms_student_url',
    'classrooms_instructor_url',
    'v_ilt_id',
    'v_ilt_meeting_id',
    'v_ilt_meeting_creation_status',
] as const;

export interface ActivityState extends GenericStoreState {
    selectedActivity: SelectedActivityResponseData | null;
    isLoaded: boolean;
    isNewActivity: boolean;
    requireUpdate: boolean;
    previousTimeStamp: number | null;
    orderItemVersions?: Array<{
        order_item_id: string;
        order_item_version: number;
        sfdc_line_item_id?: string;
    }>;
    /**
     * Cross-subgraph detail enrichment fetched from the Supergraph after the
     * REST load (CSAT, per-org counts). Null until loaded; fail-open on error.
     */
    detailEnrichment: ActivityDetailEnrichment | null;
    /** Per-learner attendance roster from AMS (attendance panel). */
    attendanceRoster: LearnerAttendance[] | null;
    /** True while the attendance roster is being fetched. */
    isAttendanceLoading: boolean;
    /**
     * Registration roster from RMS (roster panel). Cached in the store so
     * switching away from and back to the Roster tab doesn't refetch. Null
     * until loaded; fail-open. `undefined` entry vs [] distinguishes
     * "not loaded" from "loaded, empty" — we use null for not-loaded and an
     * error flag for failures.
     */
    registrationRoster: RosterEntry[] | null;
    /** True while the registration roster is being fetched. */
    isRegistrationRosterLoading: boolean;
    /** True when the registration roster fetch failed (fail-open notice). */
    registrationRosterError: boolean;
}

export const initialState = {
    selectedActivity: null,
    error: null,
    isLoading: false,
    isLoaded: false,
    isNewActivity: false,
    requireUpdate: false,
    previousTimeStamp: null,
    orderItemVersions: undefined,
    detailEnrichment: null,
    attendanceRoster: null,
    isAttendanceLoading: false,
    registrationRoster: null,
    isRegistrationRosterLoading: false,
    registrationRosterError: false,
} as ActivityState;

/**
 * activitySlice manages all activity state, and contains activity actions as well as activity state reducers.
 * Note that while the logic in the reducers appears to mutate the state, it does not.
 * The redux toolkit uses Immer to ensure that no mutations occur.
 */
export const selectedActivitySlice = createSlice({
    name: 'selectedActivity',
    initialState: initialState,
    reducers: {
        setSelectedActivity: (
            state,
            action: PayloadAction<SelectedActivityResponseData | null>,
        ) => {
            state.selectedActivity = action.payload;
        },
        /**
         * Merges only the backend-owned classroom / vILT provisioning fields
         * (CLASSROOM_STATUS_POLL_FIELDS) from a freshly fetched activity into the
         * current selectedActivity, leaving all other (user-editable) fields
         * intact. Used by the classroom status poll so an in-flight backend read
         * cannot revert a value the user just saved.
         */
        patchClassroomStatusFields: (
            state,
            action: PayloadAction<Partial<ActivityData>>,
        ) => {
            const current = state.selectedActivity?.activity;
            if (!current) {
                return;
            }
            for (const field of CLASSROOM_STATUS_POLL_FIELDS) {
                if (field in action.payload) {
                    (current as any)[field] = (action.payload as any)[field];
                }
            }
        },
        setError: (state, action: PayloadAction<any>) => {
            state.error = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsLoaded: (state, action: PayloadAction<boolean>) => {
            state.isLoaded = action.payload;
        },
        setRequireUpdate: (state, action: PayloadAction<boolean>) => {
            state.requireUpdate = action.payload;
        },
        setIsNewActivity: (state, action: PayloadAction<boolean>) => {
            state.isNewActivity = action.payload;
        },
        setPreviousBETimeStamp: (
            state,
            action: PayloadAction<number | null>,
        ) => {
            state.previousTimeStamp = action.payload;
        },
        setDetailEnrichment: (
            state,
            action: PayloadAction<ActivityDetailEnrichment | null>,
        ) => {
            state.detailEnrichment = action.payload;
        },
        setAttendanceRoster: (
            state,
            action: PayloadAction<LearnerAttendance[] | null>,
        ) => {
            state.attendanceRoster = action.payload;
        },
        setIsAttendanceLoading: (state, action: PayloadAction<boolean>) => {
            state.isAttendanceLoading = action.payload;
        },
        setRegistrationRoster: (
            state,
            action: PayloadAction<RosterEntry[] | null>,
        ) => {
            state.registrationRoster = action.payload;
        },
        setIsRegistrationRosterLoading: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.isRegistrationRosterLoading = action.payload;
        },
        setRegistrationRosterError: (state, action: PayloadAction<boolean>) => {
            state.registrationRosterError = action.payload;
        },
        setOrderItemVersions: (
            state,
            action: PayloadAction<
                | Array<{
                      order_item_id: string;
                      order_item_version: number;
                      sfdc_line_item_id?: string;
                  }>
                | undefined
            >,
        ) => {
            state.orderItemVersions = action.payload;
        },
    },
});

export const {
    setSelectedActivity,
    patchClassroomStatusFields,
    setError,
    setIsLoading,
    setIsLoaded,
    setRequireUpdate,
    setIsNewActivity,
    setPreviousBETimeStamp,
    setOrderItemVersions,
    setDetailEnrichment,
    setAttendanceRoster,
    setIsAttendanceLoading,
    setRegistrationRoster,
    setIsRegistrationRosterLoading,
    setRegistrationRosterError,
} = selectedActivitySlice.actions;

export const createActivity = (formValues: CreateActivityData) => {
    return async (dispatch: AppDispatch): Promise<any> => {
        dispatch(setIsLoading(true));

        let newActivityData = {
            ...formValues,
        } as CreateActivityData;

        try {
            const {
                result: { activity_id },
            }: HandleRequestData<CreateActivityResponseData> =
                await scheduleManagementApi.createActivity(
                    newActivityData as CreateActivityData,
                );
            dispatch(setIsLoading(false));
            return {
                isSuccessful: true,
                createdActivityId: activity_id,
            };
        } catch (error: unknown) {
            dispatch(setIsLoading(false));
            if (error instanceof ApiError) {
                const errorCode = error.response?.response_code;
                const errorText =
                    error.response?.response_text || error.message;
                return {
                    isSuccessful: false,
                    statusCode: error.statusCode,
                    errorCode,
                    errorText,
                };
            } else {
                return { isSuccessful: false };
            }
        }
    };
};

type Options = {
    triggerLoadingState?: boolean;
};

/**
 * getSelectedActivity is an async action used to fetch activity data.
 * There is no explicit inclusion of redux-thunk logic, as the redux toolkit takes care of this for us.
 */
export const getSelectedActivity = (id: string, options?: Options) => {
    const { triggerLoadingState = true } = options || {};
    return async (dispatch: AppDispatch, getState: () => GlobalState) => {
        triggerLoadingState && dispatch(setIsLoading(true));
        const state = getState();
        try {
            const { result }: HandleRequestData<SelectedActivityResponseData> =
                await scheduleManagementApi.getActivityById({
                    id,
                } as ScheduleManagementAPIQueryParams.GetActivityById);
            if (result.activity) {
                dispatch(setSelectedActivity(result));
                dispatch(setRequireUpdate(false));
                // Clear the previous activity's Supergraph enrichment so a
                // newly-loaded activity never shows the prior one's detail
                // enrichment or rosters while its own loads.
                dispatch(setDetailEnrichment(null));
                dispatch(setAttendanceRoster(null));
                dispatch(setRegistrationRoster(null));
                dispatch(setRegistrationRosterError(false));

                // Feature flag check for order item version tracking
                const featureFlags = state.features?.featureFlags;
                const isVersionTrackingEnabled = checkFeature(
                    '',
                    { featureName: 'order-item-version-tracking' },
                    featureFlags?.features,
                );

                if (isVersionTrackingEnabled) {
                    const orderItemVersions = extractOrderItemVersions(
                        result.activity.customers,
                    );
                    if (orderItemVersions.length > 0) {
                        dispatch(setOrderItemVersions(orderItemVersions));
                    }
                }
            }
        } catch (error: unknown) {
            let errorInfo;
            if (error instanceof ApiError && error.response) {
                errorInfo = {
                    isSuccessful: false,
                    statusCode: error.statusCode,
                    message: error.response.message,
                    responseCode: error.response.response_code,
                    responseText: error.response.response_text,
                };
            } else {
                errorInfo = {
                    isSuccessful: false,
                    message:
                        error instanceof Error
                            ? error.message
                            : 'An unknown error occurred',
                };
            }
            dispatch(setError(errorInfo));
        }
        triggerLoadingState && dispatch(setIsLoading(false));
    };
};

/**
 * refreshClassroomStatus fetches the activity and patches ONLY the backend-owned
 * classroom / vILT provisioning fields into the current selectedActivity. Unlike
 * getSelectedActivity (which replaces the whole record), this preserves any
 * user-editable fields the user may have just changed, so the background
 * classroom status poll cannot revert an unsaved-through/eventually-consistent
 * value (e.g. billing_status, class_size, attended).
 */
export const refreshClassroomStatus = (id: string) => {
    return async (dispatch: AppDispatch) => {
        try {
            const { result }: HandleRequestData<SelectedActivityResponseData> =
                await scheduleManagementApi.getActivityById({
                    id,
                } as ScheduleManagementAPIQueryParams.GetActivityById);
            if (result?.activity) {
                dispatch(patchClassroomStatusFields(result.activity));
            }
        } catch (error: unknown) {
            // Polling is best-effort; swallow errors so a transient failure does
            // not surface an error banner or disrupt the user's in-progress edits.
        }
    };
};

/**
 * enrichActivityDetail fetches cross-subgraph enrichment for the detail page
 * (CSAT, per-org counts) in one federated query. Fail-open: on error the
 * panels simply don't render and the REST-based page is unaffected.
 */
export const enrichActivityDetail = () => {
    return async (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        if (!isSupergraphEnrichmentEnabled(state.features?.featureFlags)) {
            return;
        }
        const activity = state.selectedActivity?.selectedActivity?.activity;
        if (!activity?.pk) {
            return;
        }
        try {
            const enrichment = await supergraphApi.getActivityDetailEnrichment(
                activity.pk,
            );
            // Bail if the user navigated away while this was in flight, so we
            // don't show this activity's enrichment against a different one.
            const current =
                getState().selectedActivity?.selectedActivity?.activity;
            if (current?.pk === activity.pk) {
                dispatch(setDetailEnrichment(enrichment));
            }
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error(
                '[supergraph] detail enrichment failed',
                error?.message ?? error,
            );
        }
    };
};

/**
 * loadAttendanceRoster fetches the per-learner, per-session attendance
 * records from AMS for the attendance panel. Loaded on demand (when the
 * panel opens), not on every detail-page load. Fail-open: the panel shows
 * an unavailable state and the page keeps working.
 */
export const loadAttendanceRoster = () => {
    return async (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        if (!isSupergraphEnrichmentEnabled(state.features?.featureFlags)) {
            return;
        }
        const activity = state.selectedActivity?.selectedActivity?.activity;
        if (!activity?.pk) {
            return;
        }
        dispatch(setIsAttendanceLoading(true));
        try {
            const roster = await supergraphApi.getClassAttendance(activity.pk);
            // Bail if the user navigated away while this was in flight, so we
            // don't show this activity's roster against a different one.
            const current =
                getState().selectedActivity?.selectedActivity?.activity;
            if (current?.pk === activity.pk) {
                dispatch(setAttendanceRoster(roster));
            }
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error(
                '[supergraph] attendance roster failed',
                error?.message ?? error,
            );
            // Same stale-navigation guard as the success path: don't clear a
            // newly-loaded activity's roster if this older fetch rejects late.
            const current =
                getState().selectedActivity?.selectedActivity?.activity;
            if (current?.pk === activity.pk) {
                dispatch(setAttendanceRoster(null));
            }
        } finally {
            // Guard the loading reset too: a late older request must not clear
            // the spinner while a newer activity's roster is still loading.
            const current =
                getState().selectedActivity?.selectedActivity?.activity;
            if (current?.pk === activity.pk) {
                dispatch(setIsAttendanceLoading(false));
            }
        }
    };
};

/**
 * loadRegistrationRoster fetches the registration roster from RMS for the
 * roster panel. Cached in the store (like the attendance roster) so switching
 * away from and back to the Roster tab does not refetch. Fail-open: on error
 * the panel shows an unavailable notice and the page keeps working.
 */
export const loadRegistrationRoster = () => {
    return async (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        if (!isSupergraphEnrichmentEnabled(state.features?.featureFlags)) {
            return;
        }
        const activity = state.selectedActivity?.selectedActivity?.activity;
        if (!activity?.pk) {
            return;
        }
        dispatch(setIsRegistrationRosterLoading(true));
        dispatch(setRegistrationRosterError(false));
        try {
            const roster = await supergraphApi.getRegistrationRoster(
                activity.pk,
            );
            // Bail if the user navigated away while this was in flight, so we
            // don't show this activity's roster against a different one.
            const current =
                getState().selectedActivity?.selectedActivity?.activity;
            if (current?.pk === activity.pk) {
                dispatch(setRegistrationRoster(roster));
            }
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error(
                '[supergraph] registration roster failed',
                error?.message ?? error,
            );
            // Same stale-navigation guard as the success path.
            const current =
                getState().selectedActivity?.selectedActivity?.activity;
            if (current?.pk === activity.pk) {
                dispatch(setRegistrationRosterError(true));
            }
        } finally {
            // Guard the loading reset too: a late older request must not clear
            // the spinner while a newer activity's roster is still loading.
            const current =
                getState().selectedActivity?.selectedActivity?.activity;
            if (current?.pk === activity.pk) {
                dispatch(setIsRegistrationRosterLoading(false));
            }
        }
    };
};

type UpdateOptions = {
    triggerLoadingState?: boolean;
};

export const updateSelectedActivity =
    (id: string, activity: Partial<ActivityData>, options?: UpdateOptions) =>
    async (
        dispatch: AppDispatch,
        getState: () => GlobalState,
    ): Promise<{
        isSuccessful: boolean;
        statusCode?: number;
        errorMessage?: string;
    }> => {
        const { triggerLoadingState = true } = options || {};
        const state = getState();
        triggerLoadingState && dispatch(setIsLoading(true));

        if (!id) {
            // eslint-disable-next-line no-console
            console.error(
                `Error updating activity: required field id was missing`,
            );
            dispatch(setIsLoading(false));
            return { isSuccessful: false, errorMessage: 'Missing field id' };
        }

        // remove properties that aren't allowed in update
        const {
            record_type,
            created_by,
            created_timestamp,
            modified_timestamp,
            modified_by,
            record_ttl,
            pk,
            v_ilt_meeting_creation_status,
            classrooms_arn,
            classrooms_student_url,
            classrooms_instructor_url,
            is_cpi_activity,
            ...data
        } = activity;

        // Strip backend-enriched response-only fields that are not part of the
        // activity schema and would be rejected by the update endpoint.
        if (data.customers) {
            data.customers = data.customers.map((customer: any) => {
                const { organization_learning_goals, ...rest } = customer;
                return rest;
            });
        }

        // billing_invoices come back from the GET carrying duplicate billing
        // identifiers written by the unified billing sync. They are absent from
        // the Invoice model and rejected by the update schema; the modeled
        // equivalents are the nmbs_* fields.
        if (data.billing_invoices) {
            data.billing_invoices = data.billing_invoices.map(
                (invoice: any) => {
                    const {
                        agreement_id,
                        bill_id,
                        invoice_id,
                        payment_method,
                        ...rest
                    } = invoice;
                    return rest;
                },
            );
        }

        // Filter VCMS-managed fields based on v_ilt_type
        // v_ilt_meeting_creation_status is always excluded (handled above)
        // v_ilt_id and v_ilt_meeting_id are only allowed when v_ilt_type is "Other"
        const vIltType =
            data.v_ilt_type ||
            state.selectedActivity.selectedActivity?.activity?.v_ilt_type;
        if (vIltType !== 'Other') {
            // Omit fields instead of setting to null to avoid triggering VCMS processing
            delete data.v_ilt_id;
            delete data.v_ilt_meeting_id;
        }

        // Feature flag check for order item version tracking
        const featureFlags = state.features?.featureFlags;
        const isVersionTrackingEnabled = checkFeature(
            '',
            { featureName: 'order-item-version-tracking' },
            featureFlags?.features,
        );

        // If we have order item versions stored and feature is enabled
        if (
            isVersionTrackingEnabled &&
            state.selectedActivity.orderItemVersions?.length > 0
        ) {
            // If customers are being updated, apply the versions to them
            if (data.customers) {
                data.customers = mapVersionsToCustomers(
                    data.customers,
                    state.selectedActivity.orderItemVersions,
                );
            }
            // If customers aren't being updated but we have stored versions, include the original customers with versions
            else if (
                state.selectedActivity.selectedActivity?.activity?.customers
            ) {
                // Only include customers data if it's not already in the update payload
                data.customers =
                    state.selectedActivity.selectedActivity.activity.customers;
            }
        }

        // Set the catalog item ID from course data when a course is assigned
        // but only if the catalog item ID is NOT already set on the activity
        // This is needed for migrating activities from Kiku to myClass LMS
        const existingCatalogItemId =
            state.selectedActivity.selectedActivity?.activity
                ?.course_catalog_item_id;
        const courseName =
            data.course_name ||
            state.selectedActivity.selectedActivity?.activity?.course_name;

        // Only look up and set catalog item ID if it's not already set
        // and the courses slice is available in the store
        if (courseName && !existingCatalogItemId && state.courses) {
            // Check if courses are loaded, if not fetch them first
            let courseList = selectAllActiveCourses(state);
            if (!selectIsCoursesLoaded(state) || courseList?.length === 0) {
                // Fetch courses synchronously before continuing
                await dispatch(getCoursesList() as any);
                // Get updated state after fetch
                const updatedState = getState();
                courseList = selectAllActiveCourses(updatedState);
            }
            if (courseList?.length > 0) {
                const courseItem = courseList.find(
                    (course: CourseItemData) => course.course === courseName,
                );
                if (courseItem?.tc3_catalog_item_id) {
                    data.course_catalog_item_id =
                        courseItem.tc3_catalog_item_id;
                    data.catalog_item_versioned_id =
                        courseItem.catalog_item_versioned_id || null;
                }
            }
        }

        try {
            await scheduleManagementApi.updateActivityById({
                id,
                activity: data as ActivityData,
            });

            const newActivity = {
                message: state.selectedActivity.selectedActivity?.message,
                activity: {
                    ...state.selectedActivity.selectedActivity?.activity,
                    ...activity,
                },
            } as SelectedActivityResponseData;

            dispatch(setSelectedActivity(newActivity));

            dispatch(setIsLoading(false));
            return { isSuccessful: true };
        } catch (error: any) {
            dispatch(setIsLoading(false));
            if (error instanceof ApiError) {
                return {
                    isSuccessful: false,
                    statusCode: error.statusCode,
                    errorMessage:
                        error.response?.response_text || error.message,
                };
            }
            return {
                isSuccessful: false,
                errorMessage: 'An error occurred',
            };
        }
    };

export const selectSelectedActivity = (state: GlobalState) =>
    state.selectedActivity.selectedActivity;
export const selectDetailEnrichment = (state: GlobalState) =>
    state.selectedActivity.detailEnrichment;
export const selectAttendanceRoster = (state: GlobalState) =>
    state.selectedActivity.attendanceRoster;
export const selectIsAttendanceLoading = (state: GlobalState) =>
    state.selectedActivity.isAttendanceLoading;
export const selectRegistrationRoster = (state: GlobalState) =>
    state.selectedActivity.registrationRoster;
export const selectIsRegistrationRosterLoading = (state: GlobalState) =>
    state.selectedActivity.isRegistrationRosterLoading;
export const selectRegistrationRosterError = (state: GlobalState) =>
    state.selectedActivity.registrationRosterError;
export const selectError = (state: GlobalState) => state.selectedActivity.error;
export const selectIsLoading = (state: GlobalState) =>
    state.selectedActivity.isLoading;
export const selectIsLoaded = (state: GlobalState) =>
    state.selectedActivity.isLoaded;
export const selectOrderItemVersions = (state: GlobalState) =>
    state.selectedActivity.orderItemVersions;

export default selectedActivitySlice.reducer;
